import { css } from '@emotion/react'
import { Plural, t, Trans } from '@lingui/macro'
import { brandColors } from 'front-end/../shared/basicPalette'
import { Box, Heading, Link, List } from 'front-end/src/components'
import { EventModelAsStudent } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { getAttendees } from 'front-end/src/pages/manage-events/eventUtils'
import React, { useState } from 'react'
import dayjs from 'shared/dayjs'
import EventInvitee from '../event-invitee/EventInvitee'
import { getStatus } from '../event-status/EventStatus'
import { RSVPButton } from '../rsvp-buttons/RSVPButtons'

export const isRegistrationClosed = (event) => {
  return event.end_time && dayjs(event.end_time).isBefore(dayjs())
}

const NUMBER_OF_SHOWN_ITEMS = 7
const HEIGHT_OF_ROW_USER = 43

const EventAttendance = (props: {
  disableRegistration?: boolean
  event: EventModelAsStudent
  currentUserInvitee:
    | {
        attending: boolean
        last_edit_by: number
      }
    | undefined
    | null
  fetchEvent: () => void
}) => {
  const [show, setShow] = useState(false)

  const AttendanceCount = () => {
    const { event } = props
    const attendees = getAttendees(event)

    return (
      <Box mt="24px" color={brandColors.grey[500]} fontSize="14px">
        {getStatus(event) === 'completed' ? (
          <Plural
            value={attendees.length}
            _0="No one is going"
            one="# person went:"
            other="# people went:"
          />
        ) : (
          <Plural
            value={attendees.length}
            _0="No one is going"
            one="# person attending:"
            other="# people attending:"
          />
        )}
      </Box>
    )
  }

  const AttendanceTitle = () => {
    const { event, currentUserInvitee } = props

    const attendees = getAttendees(event)

    let message = null
    const title =
      getStatus(event) === 'completed'
        ? currentUserInvitee && currentUserInvitee.attending
          ? t`You were interested`
          : t`You weren't interested`
        : currentUserInvitee && currentUserInvitee.attending
        ? t`You're going!`
        : t`Are you going?`

    if (event.participants_limit && event.participants_limit > 0) {
      const placesRemaining = event.participants_limit - attendees.length
      if (placesRemaining > 0) {
        // @ts-expect-error 9/11/2020
        message = (
          <Plural
            value={placesRemaining}
            _0={<em className="eventPlaces-full">No places left</em>}
            one={<em className="eventPlaces-limited"># place left</em>}
            other={<em># places left</em>}
          />
        )
      } else {
        // @ts-expect-error 9/11/2020
        message = (
          <em className="eventPlaces-full">
            <Trans>No places left</Trans>
          </em>
        )
      }
    }
    return (
      <Box mb="24px">
        <Heading as="h3" fontSize="24px">
          {title}
        </Heading>
        {message}
      </Box>
    )
  }

  const { event, fetchEvent } = props
  if (!event) {
    return null
  }
  let { attendedInvitees } = event

  attendedInvitees = attendedInvitees.filter(({ attending }) => {
    return attending
  })
  const userInvitees = attendedInvitees.map((invitee) => {
    const { user } = invitee
    const isFacilitator = event.facilitator?.id === user.id
    return (
      <EventInvitee
        key={user.id}
        user={user}
        attending={invitee.attending}
        isFacilitator={isFacilitator}
      />
    )
  })
  const isClosed = getStatus(event) === 'completed'
  const rsvpNotAnswered = event?.currentUserInvitee?.attending === undefined
  let placesLeft: number | null = null
  if (event.participants_limit && event.participants_limit > 0) {
    placesLeft = event.participants_limit - attendedInvitees.length
  }

  return (
    <Box w="100%" className="eventDetail-attendance">
      {!(isClosed && rsvpNotAnswered) && (
        <>
          <AttendanceTitle />
          <RSVPButton
            event={event}
            placesLeft={placesLeft}
            refetch={fetchEvent}
            width="100%"
            css={css`
              button {
                width: 50%;
              }
            `}
          />
        </>
      )}
      <AttendanceCount />
      <Box
        mt="16px"
        overflow="hidden"
        height={
          show || userInvitees.length < NUMBER_OF_SHOWN_ITEMS
            ? undefined
            : HEIGHT_OF_ROW_USER * NUMBER_OF_SHOWN_ITEMS
        }
      >
        <List m={0} p={0}>
          {userInvitees.length === 0 ? (
            <Box>
              <Trans>No attendees yet</Trans>
            </Box>
          ) : (
            userInvitees
          )}
        </List>
      </Box>
      {!show && userInvitees.length > NUMBER_OF_SHOWN_ITEMS && (
        <Link onClick={() => setShow(true)}>Show all</Link>
      )}
    </Box>
  )
}

export default EventAttendance
